export const VISUAL_REALISM_QUERY_PARAMETER = "realism";

/**
 * Render-only realism treatment for the accepted M5 world. The values are
 * centralized so the look can be A/B reviewed without touching geography,
 * simulation, collision, movement, or authored asset transforms.
 */
export const VISUAL_REALISM_CONFIG = Object.freeze({
  enabledByDefault: true,
  surfaceTextureRepeat: 2,
  landCoverTextureModuleM: 4,
  centralStreetTreeBase: Object.freeze({
    surfaceLiftM: 0.014,
    depthM: 0.028,
    minimumRadiusM: 0.42,
    maximumRadiusM: 0.62,
    clearanceFromRoadEdgeM: 5,
    radialSegments: 10,
    scaleX: 0.92,
    scaleZ: 1.06,
  }),
  normalScale: Object.freeze({
    facade: 0.26,
    plasterMask: 0.18,
    architecturalTrim: 0.12,
    landCover: 0.14,
    treeBark: 0.22,
    oldTownStone: 0.18,
    asphalt: 0.22,
    concrete: 0.2,
    aggregate: 0.28,
    wood: 0.24,
    clayRoof: 0.3,
    metalRoof: 0.16,
  }),
  facadeRoughnessMap: Object.freeze({
    glassLuma: 82,
    mullionLuma: 230,
    frameLuma: 238,
    wallLuma: 250,
    seamLuma: 255,
  }),
  surfaceJointLuma: Object.freeze({
    roof: 182,
    cobble: 152,
    cement: 190,
    wood: 138,
    paver: 178,
    concrete: 204,
  }),
  facadeWindowEmissionScale: 0.55,
  authoredWindowEmissionScale: 0.38,
  facadeWindowEmissionColor: 0xffd0a3,
  authoredWindowEmissionColor: 0xf6c995,
  authoredGlassColor: 0x425256,
  panelAuthoredGlassColor: 0x718083,
  facadeGlass: Object.freeze({ red: 145, green: 160, blue: 168 }),
  panelFacadeGlass: Object.freeze({ red: 185, green: 194, blue: 198 }),
  drySurface: Object.freeze({
    terrainRoughness: 0.98,
    landCoverRoughness: 0.96,
    asphaltRoughness: 0.94,
    stoneRoughness: 0.92,
    concreteRoughness: 0.94,
    aggregateRoughness: 0.98,
    woodRoughness: 0.9,
    markingRoughness: 0.82,
  }),
  roofSurface: Object.freeze({
    clayRoughness: 0.92,
    metalRoughness: 0.72,
    metalness: 0.22,
  }),
  wetSurface: Object.freeze({
    shadowColor: 0x17201e,
    terrainRoughness: 0.88,
    terrainDarkening: 0.12,
    landCoverRoughness: 0.84,
    landCoverDarkening: 0.16,
    asphaltRoughness: 0.72,
    asphaltDarkening: 0.3,
    stoneRoughness: 0.82,
    stoneDarkening: 0.26,
    concreteRoughness: 0.82,
    concreteDarkening: 0.22,
    aggregateRoughness: 0.9,
    aggregateDarkening: 0.16,
    woodRoughness: 0.78,
    woodDarkening: 0.28,
    clayRoofRoughness: 0.78,
    clayRoofDarkening: 0.18,
    metalRoofRoughness: 0.58,
    metalRoofDarkening: 0.12,
    markingRoughness: 0.72,
    markingDarkening: 0.08,
    railRoughness: 0.16,
    railDarkening: 0.08,
  }),
});

export function visualRealismEnabled(search = ""): boolean {
  const configured = new URLSearchParams(search).get(
    VISUAL_REALISM_QUERY_PARAMETER,
  );
  if (configured === null) {
    return VISUAL_REALISM_CONFIG.enabledByDefault;
  }
  return !["0", "false", "off"].includes(configured.toLowerCase());
}
